import { z } from 'zod';
/**
 * Platform-level role (spans all organizations). Only superAdmins can create
 * organizations and grant/revoke superAdmin on other users. SuperAdmin does
 * NOT imply read access to any org's data — that requires explicit membership.
 *
 * Stored on the Firebase Auth custom claim as `platformRole?: 'superAdmin'`.
 * Absence of this field means "no platform role".
 */
export const PlatformRole = z.enum(['superAdmin']);
/**
 * Role within a single organization. `admin` manages members + invites;
 * `member` is the analyst-equivalent — can create notes, briefs, investigations,
 * etc. within the org. There is intentionally no read-only `viewer` tier;
 * principals consume shared briefs via the public `/b/:token` link.
 */
export const OrgRole = z.enum(['admin', 'member']);
/**
 * Mirror of the `orgs` claim — a compact `{ [orgId]: OrgRole }` map written
 * into the Firebase Auth custom claims. Capped at 20 entries (custom claims
 * have a 1000-byte limit); for users in more than 20 orgs, rules fall back
 * to a Firestore `get()` against the member subcollection.
 */
export const OrgsClaim = z.record(z.string(), OrgRole);
/**
 * Maximum number of orgs that fit in the `orgs` custom claim. Firebase Auth
 * limits custom claims to ~1000 bytes; past 20 orgs we overflow and
 * membership for that user must be resolved via Firestore.
 */
export const ORGS_CLAIM_MAX = 20;
/**
 * Sentinel `orgId` for the shared system corpus — Documents, edges, and
 * Candidates ingested by superAdmins via the platform-level pipeline are
 * stamped with this value. `visibleOrgIdsFor(activeOrgId)` always
 * includes it so every member sees system data on top of their own
 * org's slice. The string literal is mirrored once in `firestore.rules`
 * (synthesis read gate) — change both if it ever moves.
 */
export const SYSTEM_ORG_ID = 'system';
/**
 * Organization settings — per-org knobs the admin can tune.
 *
 *   emailDomains: allowlist for invites; if present+non-empty, invites can
 *                 only be sent to addresses on these domains. Empty array or
 *                 missing = allow any domain.
 *   timezone:     IANA tz string, used for activity timestamps etc.
 */
export const OrgSettings = z.object({
    emailDomains: z.array(z.string()).default([]),
    timezone: z.string().default('UTC'),
});
/**
 * A tenant. Stored at `organizations/{orgId}` in Firestore. The `id` doubles
 * as the Firestore doc key; the `slug` is a URL-friendly display form
 * (lowercase a-z, 0-9, hyphens — enforced by the API, not by rules).
 *
 * `createdBy` is the superAdmin uid that provisioned the org; that uid is
 * also seeded as the first org admin (see `organizations/{orgId}/members`).
 */
export const Organization = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    slug: z
        .string()
        .min(1)
        .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/),
    createdAt: z.string(),
    createdBy: z.string(),
    settings: OrgSettings.default({
        emailDomains: [],
        timezone: 'UTC',
    }),
    deletedAt: z.string().nullable().optional(),
});
/**
 * Membership document. Lives in the subcollection
 * `organizations/{orgId}/members/{uid}` so Firestore rules can cheaply walk
 * `get(/databases/$(db)/documents/organizations/$(orgId)/members/$(uid))`
 * when the `orgs` claim is stale or overflowed.
 *
 * `displayName` + `email` are denormalized from Firebase Auth so the members
 * table can render without fan-out reads. Refreshed by the server whenever
 * a user is added or their auth profile changes.
 */
export const OrgMember = z.object({
    uid: z.string(),
    role: OrgRole,
    joinedAt: z.string(),
    invitedBy: z.string().optional(),
    displayName: z.string().nullable().optional(),
    email: z.string().email().nullable().optional(),
    /**
     * Optional team / department label (e.g. "Eng", "GTM", "Finance").
     * Drives the People-page filter chips. Free-text rather than enum so
     * each org can shape its own org chart without a schema change.
     */
    team: z.string().max(80).optional(),
    /**
     * Optional one-line summary of what this person owns (scorecard
     * metrics, rocks, watchlist entries). E.g. "Brand · vision · cap raise".
     * Surfaces in the People table's "Owns" column and the dossier rail.
     */
    ownerOf: z.string().max(280).optional(),
    /**
     * ISO timestamp of the user's last in-app activity. Server-stamped on
     * any audit event; reads on the People page surface as a relative
     * time. Optional because pre-existing members may not have signed in
     * since the field was introduced.
     */
    lastActiveAt: z.string().optional(),
});
/** Compact org descriptor returned on the auth bootstrap + org-list APIs. */
export const OrgSummary = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    role: OrgRole,
});
/** Request body for `POST /api/orgs` (superAdmin-only). */
export const CreateOrgRequest = z.object({
    name: z.string().min(1).max(100),
    slug: z
        .string()
        .min(1)
        .max(50)
        .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/),
    firstAdminUid: z.string().optional(),
});
/**
 * Cap on number of orgs a single user can create. Each org is its own Stripe
 * subscription, so unlimited would invite trial abuse; 10 matches the
 * Enterprise-tier "multi-business operators and PE platforms" use case in
 * apps/marketing/src/components/pricing.tsx. SuperAdmins can override for
 * legit cases via the platform-superadmin endpoints.
 */
export const MAX_ORGS_PER_USER = 10;
/**
 * Slugs that collide with system-orgIds or top-level marketing/auth routes.
 * Org-create rejects these with `reason: 'reserved'`.
 *
 * Typed as `readonly [...] as const` so the set membership is type-level
 * visible (not just a runtime check) — `.includes()` narrows at the call
 * site, and the `SlugReservedWord` literal-union is exportable for any
 * downstream consumer that wants the closed set as a type. Matches the
 * project convention of promoting closed string sets to closed types
 * (CLAUDE.md: "closed string sets are enums").
 */
export const SLUG_RESERVED_WORDS = [
    'admin',
    'api',
    'system',
    'public',
    'internal',
    'leanopx',
    'support',
    'help',
    'about',
    'sign-in',
    'sign-up',
    'auth',
];
/**
 * Response from `GET /api/orgs/slug-availability?slug=<s>`.
 *
 *   - `taken`    — a different org already owns the slug.
 *   - `invalid`  — slug fails the schema regex (lowercase alphanumeric +
 *                  hyphens, 1–50 chars, no leading/trailing hyphens).
 *   - `reserved` — slug is in the reserved-words allowlist (e.g. 'admin',
 *                  'api', 'system' — collides with system-orgIds).
 */
export const SlugAvailabilityResponse = z.object({
    available: z.boolean(),
    reason: z.enum(['taken', 'invalid', 'reserved']).optional(),
});
/** Request body for `PATCH /api/orgs/:id` (orgAdmin-only). */
export const UpdateOrgRequest = z.object({
    name: z.string().min(1).max(100).optional(),
    settings: OrgSettings.partial().optional(),
});
/**
 * Request body for `PATCH /api/orgs/:id/members/:uid` (orgAdmin-only).
 *
 * `role` mutations refresh the user's `orgs` claim (see `refreshUserClaims`);
 * the optional `team` / `ownerOf` fields are presentation-only and don't
 * touch claims. `null` on either explicitly clears the value (omit to leave
 * the existing value alone).
 */
export const UpdateMemberRequest = z.object({
    role: OrgRole.optional(),
    team: z.string().max(80).nullable().optional(),
    ownerOf: z.string().max(280).nullable().optional(),
});
/** Response row for `GET /api/platform/users` (superAdmin-only listing). */
export const PlatformUserRow = z.object({
    uid: z.string(),
    email: z.string().email().nullable(),
    displayName: z.string().nullable(),
    platformRole: PlatformRole.nullable(),
    orgCount: z.number().int().nonnegative(),
});
/** Response shape for `GET /api/orgs`. */
export const ListOrgsResponse = z.object({
    organizations: z.array(OrgSummary),
    primaryOrgId: z.string().nullable().optional(),
});
